import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { MessageAlertService, MessageSnackBarService } from "@obg/mat.ui.core";
import { batchActions } from "@obg/ngrx-utils";
import { GroupIBFraudPreventionService, isEmailValid } from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	CORE_ROUTE,
	CoreState,
	CustomerManagementSelector,
	LoginRequest,
	MessageAction,
	MessageSelector,
	MessageType,
	SESSION_ACTION_TYPE,
	SessionSelector,
} from "@obg/ngx.core";
import { RouteAction } from "@obg/ngx.router";
import { isEmpty, isNil } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import {
	ChallengeDetails,
	LoginFeatureState,
	SUSPICIOUS_LOGIN_ACTION_TYPE,
	SuspiciousLoginAction,
	SuspiciousLoginSelector,
} from "../../sdk";

import { OtherChannelOptions, VerifyLoginStep } from "./suspicious-login.model";

const LOGIN_TRANSLATION_PREFIX = "login";
const SUSPICIOUS_TRANSLATION_PREFIX = "suspicious-login.send-code";

@Component({
	selector: "obg-m-suspicious-login-container",
	templateUrl: "./suspicious-login.container.html",
	styleUrls: ["./suspicious-login.container.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class SuspiciousLoginContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-suspicious-login") hostCssClass = true;

	@Output() loginSuccess = new EventEmitter();
	@Output() busy = new EventEmitter<boolean>();

	challengeDetails: ChallengeDetails | undefined;
	verifyLoginSteps = VerifyLoginStep;
	currentStep = VerifyLoginStep.RequestCode;
	messages: (() => void) | undefined;
	showErrorMessage: boolean | undefined;

	private data$$: Subscription | undefined;
	private credentials: LoginRequest | undefined;

	constructor(
		private store: Store<LoginFeatureState & CoreState>,
		private sessionSelector: SessionSelector,
		private messageSelector: MessageSelector,
		private messageAction: MessageAction,
		private messageSnackBarService: MessageSnackBarService,
		private messageAlertService: MessageAlertService,
		private suspiciousLoginAction: SuspiciousLoginAction,
		private changeDetector: ChangeDetectorRef,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService
	) {}

	ngOnInit() {
		const challengeDetails$ = this.store.pipe(
			select(SuspiciousLoginSelector.getChallengeDetails),
			filter((challengeDetails) => !!challengeDetails),
			tap((challengeDetails) => (this.challengeDetails = challengeDetails))
		);

		const requestPinCodeSuccess$ = this.messageSnackBarService
			.presentOnDispatch(
				{
					context: SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin,
					messageType: MessageType.Success,
				},
				{ translationPrefix: SUSPICIOUS_TRANSLATION_PREFIX }
			)
			.pipe(tap(() => (this.currentStep = VerifyLoginStep.ValidateCode)));

		const requestPinCodeFail$ = this.store.pipe(
			this.messageSelector.getAll$({
				context: SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin,
				messageType: MessageType.Error,
			}),
			filter((error) => !isEmpty(error)),
			tap(() => (this.showErrorMessage = true))
		);

		const invalidCredentials$ = this.store.pipe(
			this.messageSelector.getMessageCodes$({
				messageCode: AUTH_MESSAGE_CODES.login.invalidCredentials,
			}),
			filter((error) => !isEmpty(error)),
			tap(() => (this.showErrorMessage = true))
		);

		const loginError$ = this.messageAlertService.presentOnDispatch(
			{
				context: AUTH_MESSAGE_CONTEXT.loginWithVerificationCode,
				messageType: MessageType.Error,
				excludeCodes: [
					AUTH_MESSAGE_CODES.login.invalidCredentials,
					AUTH_MESSAGE_CODES.login.termsAndConditionsNotAccepted,
					AUTH_MESSAGE_CODES.login.latestTermsAndConditionsNotAccepted,
					AUTH_MESSAGE_CODES.login.consentsNotAccepted,
					AUTH_MESSAGE_CODES.login.latestEntityLegalNoticeNotAccepted,
					AUTH_MESSAGE_CODES.login.latestPrivacyPolicyNotAccepted,
				],
			},
			{
				fallbackMessageCode: AUTH_MESSAGE_CODES.login.error,
				title: "title.login-error",
				translationPrefix: LOGIN_TRANSLATION_PREFIX,
			}
		);

		const isValidated$ = this.messageSnackBarService
			.presentOnDispatch(
				{
					context: SESSION_ACTION_TYPE.loginWithVerificationCode,
					messageType: MessageType.Success,
				},
				{ translationPrefix: LOGIN_TRANSLATION_PREFIX }
			)
			.pipe(tap(() => this.loginSuccess.emit()));

		const isViewBusy$ = merge(
			this.store.select(this.sessionSelector.isBusy()),
			this.store.select(SuspiciousLoginSelector.isBusy)
		).pipe(tap((isBusy) => this.busy.emit(isBusy)));

		const credentials$ = this.store
			.select(CustomerManagementSelector.getCredentials)
			.pipe(tap((credentials) => (this.credentials = credentials)));

		this.data$$ = merge(
			challengeDetails$,
			requestPinCodeFail$,
			requestPinCodeSuccess$,
			invalidCredentials$,
			isValidated$,
			loginError$,
			isViewBusy$,
			credentials$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.store.dispatch(this.suspiciousLoginAction.clearCommunicationChannels());
		this.clearSuspiciousContext();
		this.data$$?.unsubscribe();
		if (this.messages) {
			this.messages();
		}
	}

	onSendPin(channel: string) {
		this.clearSuspiciousContext();
		switch (channel) {
			case OtherChannelOptions.ForgotPassword: {
				let queryParams: object | undefined;
				if (!isNil(this.credentials) && this.credentials.username && isEmailValid(this.credentials.username)) {
					queryParams = { username: this.credentials.username };
				}

				this.store.dispatch(RouteAction.navigate(CORE_ROUTE.auth.forgotPassword, undefined, { queryParams }));
				break;
			}
			case OtherChannelOptions.HasCode: {
				this.currentStep = VerifyLoginStep.ValidateCode;
				break;
			}
			default: {
				this.communicationChannel(channel);
			}
		}
	}

	onValidatePin(verificationCode: string) {
		if (isNil(this.credentials) || !this.credentials.username) {
			return;
		}

		const verificationCodeLoginRequest = {
			username: this.credentials.username,
			iovationBlackBox: this.credentials.iovationBlackBox,
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			verificationCode,
		};
		this.store.dispatch(this.suspiciousLoginAction.verifyPin(verificationCodeLoginRequest));
	}

	onResendPin() {
		this.store.dispatch(this.suspiciousLoginAction.resetPin());
		this.currentStep = VerifyLoginStep.RequestCode;
		this.clearSuspiciousContext();
	}

	private communicationChannel(channel: string) {
		const token = this.challengeDetails!.token;
		const suspiciousLoginPinRequest = { channel, token };
		this.store.dispatch(this.suspiciousLoginAction.requestPin(suspiciousLoginPinRequest));
	}

	private clearSuspiciousContext() {
		this.store.dispatch(
			batchActions(
				this.messageAction.clearByContext(SESSION_ACTION_TYPE.loginWithVerificationCode),
				this.messageAction.clearByContext(SUSPICIOUS_LOGIN_ACTION_TYPE.requestPin)
			)
		);
		this.showErrorMessage = false;
	}
}
